import { useMemo } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { useAuth } from '@favelafood/shared/auth/useAuth';
import { useRoles } from '@favelafood/shared/auth/roles';
import { useResolveMerchant } from '@/kds/hooks/useResolveMerchant';
import { MERCHANT_NAV } from './nav';
import { MerchantSidebar } from '@favelafood/shared/components/merchant/shell/MerchantSidebar';
import { MerchantTopbar } from './MerchantTopbar';

/**
 * Owner (comerciante) shell. The staff branch left with the 3-app carve
 * (apps/staff, C2) — staff now logs in on its own subdomain. This shell serves
 * the owner area only until it moves to apps/comerciante (C3).
 */
export function MerchantShell(): JSX.Element {
  const roles = useRoles();
  const logout = useAuth((s) => s.logout);
  const [params] = useSearchParams();
  const merchantId = params.get('merchantId') ?? undefined;
  const isAdmin = roles.includes('admin');
  // Resolve memberships so the Topbar can render StoreSwitcher correctly.
  const resolution = useResolveMerchant(merchantId ?? null, isAdmin);
  // On auto-resolve (single store) the URL carries no `?merchantId=`, so fall back
  // to the resolved id — the Topbar toggle + owner nav links need a concrete id.
  const effectiveMerchantId =
    merchantId ?? (resolution.state === 'resolved' ? resolution.merchantId : undefined);

  // Owner nav: links must carry the selected store, else clicking them drops
  // `?merchantId=` and bounces the user back to the store picker.
  const nav = useMemo(() => {
    if (!effectiveMerchantId) return MERCHANT_NAV;
    return MERCHANT_NAV.map((section) => ({
      ...section,
      items: section.items.map((item) =>
        item.to ? { ...item, to: `${item.to}?merchantId=${effectiveMerchantId}` } : item
      ),
    }));
  }, [effectiveMerchantId]);

  return (
    <div className="merchant-theme flex min-h-screen bg-[var(--m-surface-muted)] text-[var(--m-fg)]">
      <MerchantSidebar
        badge="COMÉRCIO"
        nav={nav}
        user={{ name: 'Minha conta', role: 'Comerciante' }}
        // logout() is the sole navigator (area-aware) — racing it with a local
        // location.assign could beat keycloak.logout's redirect and leave the KC
        // SSO cookie alive.
        onLogout={logout}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <MerchantTopbar
          variant="merchant"
          merchantId={effectiveMerchantId}
          memberships={
            resolution.state === 'resolved' || resolution.state === 'select'
              ? resolution.memberships
              : undefined
          }
        />
        <main className="flex-1 overflow-auto p-6"><Outlet /></main>
      </div>
    </div>
  );
}
